import React, { useState, useMemo, useCallback } from 'react';
import { FiX, FiCopy, FiShoppingBag, FiMinus, FiPlus, FiShare, FiTrash2, FiEye } from 'react-icons/fi';
import ProductDetails from './ProductDetails';

const ShoppingListModal = ({
  isOpen,
  onClose,
  selectedItemsMap,
  formatCurrency,
  translations,
  language,
  onRemoveItem,
  onIncreaseItem,
  onDecreaseItem
}) => {
  const [copyMessage, setCopyMessage] = useState(null);
  // SHOP-135: drives the nested "view detail" overlay, stacked above this
  // modal's own overlay the same way ChooseCartItemModal stacks above
  // WatchedItemsModal (parent overlay z-index + 1000, see detailOverlayStyle).
  const [detailItem, setDetailItem] = useState(null);

  const selections = useMemo(() => Object.values(selectedItemsMap || {}), [selectedItemsMap]);

  const itemsByStore = useMemo(() => {
    return selections.reduce((acc, item, index) => {
      const store = item.store || 'Store';
      if (!acc[store]) acc[store] = [];

      acc[store].push({
        ...item,
        __itemKey: getItemKey(item, index)
      });

      return acc;
    }, {});
  }, [selections]);

  const storeTotalsCalculated = useMemo(() => {
    const totals = {};

    Object.entries(itemsByStore).forEach(([store, items]) => {
      totals[store] = items.reduce((sum, item) => {
        const qty = Number(item.quantity ?? 1);
        return sum + (Number(item.price || 0) * qty);
      }, 0);
    });

    return totals;
  }, [itemsByStore]);

  const grandTotal = useMemo(() => {
    return Object.values(storeTotalsCalculated).reduce((sum, value) => sum + value, 0);
  }, [storeTotalsCalculated]);

  const generateListText = useCallback(() => {
    if (selections.length === 0) {
      return translations.noItemsSelected;
    }

    let text = `==== ${translations.shoppingListModalTitle.toUpperCase()} ====\n\n`;

    Object.entries(itemsByStore).forEach(([store, items]) => {
      text += `🛍️ ${store}\n`;
      text += `${translations.storeSubtotal}: ${formatCurrency(storeTotalsCalculated[store] || 0)}\n`;
      text += '-----------------------------\n';

      items.forEach((item, i) => {
        const qty = Number(item.quantity ?? 1);
        const unitText = item.unit ? ` (${item.unit})` : '';
        const lineTotal = (Number(item.price || 0) * qty);

        text += `${i + 1}. x${qty} ${item.name}${unitText} - ${formatCurrency(lineTotal)}\n`;
      });

      text += '\n';
    });

    text += `${translations.totalAmountTitle} ${formatCurrency(grandTotal)}`;

    return text;
  }, [
    selections,
    translations,
    itemsByStore,
    storeTotalsCalculated,
    formatCurrency,
    grandTotal
  ]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generateListText());
      setCopyMessage(translations.copySuccess);
      setTimeout(() => setCopyMessage(null), 2000);
    } catch (error) {
      console.error('Clipboard copy failed:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={headerStyle}>
          <h3 style={titleStyle}>
            <FiShoppingBag />
            <span>{translations.shoppingListModalTitle}</span>
          </h3>

          <button onClick={onClose} style={iconButton}>
            <FiX size={20} />
          </button>
        </div>

        <div style={contentCardStyle}>
          {selections.length === 0 ? (
            <div style={emptyStateStyle}>{translations.noItemsSelected}</div>
          ) : (
            <div style={listContainerStyle}>
              {Object.entries(itemsByStore).map(([store, items]) => (
                <div key={store} style={storeSectionStyle}>
                  <div style={storeHeaderStyle}>
                    <div style={storeNameStyle}>🛍️ {store}</div>
                    <div style={storeSubtotalStyle}>
                      {translations.storeSubtotal}: {formatCurrency(storeTotalsCalculated[store] || 0)}
                    </div>
                  </div>

                  <div style={dividerStyle} />

                  {items.map((item, index) => {
                    const qty = Number(item.quantity ?? 1);
                    const lineTotal = Number(item.price || 0) * qty;

                    return (
                      <div key={item.__itemKey} style={itemRowStyle}>
                        <div style={quantityControlStyle}>
                          <button
                            type="button"
                            onClick={() => onDecreaseItem?.(item)}
                            disabled={qty <= 1}
                            aria-label={translations.decreaseQuantity}
                            title={translations.decreaseQuantity}
                            style={qty <= 1 ? qtyButtonDisabledStyle : qtyButtonStyle}
                          >
                            <FiMinus size={16} />
                          </button>

                          <div style={qtyValueStyle}>{qty}</div>

                          <button
                            type="button"
                            onClick={() => onIncreaseItem?.(item)}
                            aria-label={translations.increaseQuantity}
                            title={translations.increaseQuantity}
                            style={qtyButtonStyle}
                          >
                            <FiPlus size={16} />
                          </button>
                        </div>

                        <div style={itemInfoStyle}>
                          <div style={itemNameStyle}>
                            {index + 1}. {item.name}
                            {item.unit ? ` (${item.unit})` : ''}
                          </div>

                          <div style={itemPriceRowStyle}>
                            <span style={unitPriceStyle}>
                              {formatCurrency(Number(item.price || 0))} c/u
                            </span>
                            <span style={arrowStyle}>→</span>
                            <span style={lineTotalStyle}>
                              {formatCurrency(lineTotal)}
                            </span>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => setDetailItem(item)}
                          aria-label={translations.viewItemDetail}
                          title={translations.viewItemDetail}
                          style={detailButtonStyle}
                        >
                          <FiEye size={18} />
                        </button>

                        <button
                          type="button"
                          onClick={() => onRemoveItem?.(item)}
                          aria-label={translations.removeFromShoppingList}
                          title={translations.removeFromShoppingList}
                          style={deleteButtonStyle}
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}

              <div style={totalFooterStyle}>
                <span>{translations.totalAmountTitle}</span>
                <strong>{formatCurrency(grandTotal)}</strong>
              </div>
            </div>
          )}
        </div>

        {copyMessage && <div style={messageStyle}>{copyMessage}</div>}

        <div style={actionsStyle}>
          <button onClick={handleCopy} style={primaryButton}>
            <FiShare /> {translations.copyButton}
          </button>

          <button onClick={onClose} style={secondaryButton}>
            {translations.modalClose}
          </button>
        </div>
      </div>

      {detailItem && (
        <div
          data-testid="item-detail-overlay"
          style={detailOverlayStyle}
          onClick={(e) => { e.stopPropagation(); setDetailItem(null); }}
        >
          <div style={detailCardStyle} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setDetailItem(null)}
              aria-label={translations.closeItemDetail}
              title={translations.closeItemDetail}
              style={detailCloseButtonStyle}
            >
              <FiX size={20} />
            </button>
            <ProductDetails item={detailItem} isMain translations={translations} language={language} formatCurrency={formatCurrency} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ShoppingListModal;

function getItemKey(item, index) {
  return item.productid || item.id || `${item.store}-${item.name}-${index}`;
}

/* ---------- styles ---------- */

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.75)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
  padding: 16,
};

const modalStyle = {
  background: '#fff',
  borderRadius: 16,
  width: '100%',
  maxWidth: '760px',
  height: '90vh',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  padding: 16,
  boxSizing: 'border-box',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '1px solid #E5E7EB',
  paddingBottom: 10,
};

const titleStyle = {
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 24,
  fontWeight: 700,
};

const contentCardStyle = {
  marginTop: 12,
  flex: 1,
  border: '1px solid #E5E7EB',
  borderRadius: 14,
  background: '#F8FAFC',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const listContainerStyle = {
  padding: 16,
  overflowY: 'auto',
  flex: 1,
};

const storeSectionStyle = {
  marginBottom: 24,
};

const storeHeaderStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const storeNameStyle = {
  fontSize: 18,
  fontWeight: 700,
  color: '#0F172A',
};

const storeSubtotalStyle = {
  fontSize: 16,
  fontWeight: 600,
  color: '#111827',
};

const dividerStyle = {
  borderTop: '1px dashed #CBD5E1',
  margin: '12px 0 16px',
};

const itemRowStyle = {
  display: 'flex',
  gap: 16,
  alignItems: 'flex-start',
  marginBottom: 16,
};

const quantityControlStyle = {
  display: 'grid',
  gridTemplateRows: '40px 30px 40px',
  width: '30px',
  border: '1px solid #CBD5E1',
  borderRadius: 12,
  overflow: 'hidden',
  background: '#fff',
};

const qtyButtonStyle = {
  border: 'none',
  background: '#F8FAFC',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#334155',
};

const qtyButtonDisabledStyle = {
  ...qtyButtonStyle,
  cursor: 'not-allowed',
  color: '#CBD5E1',
};

const deleteButtonStyle = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: '#dc2626',
  padding: '6px',
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

const detailButtonStyle = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: '#2563eb',
  padding: '6px',
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

// SHOP-135: nested "view detail" overlay, stacked above this modal's own
// overlay (zIndex 1000) the same +1000 way ChooseCartItemModal (5000) stacks
// above WatchedItemsModal (4000).
const detailOverlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
  padding: 16,
};

const detailCardStyle = {
  position: 'relative',
  background: '#fff',
  borderRadius: 14,
  padding: 20,
  width: '100%',
  maxWidth: '420px',
  maxHeight: '85vh',
  overflowY: 'auto',
  boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
};

const detailCloseButtonStyle = {
  position: 'absolute',
  top: 12,
  right: 12,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: '#334155',
};

const qtyValueStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 22,
  fontWeight: 700,
  borderTop: '1px solid #CBD5E1',
  borderBottom: '1px solid #CBD5E1',
  background: '#fff',
  color: '#111827',
};

const itemInfoStyle = {
  flex: 1,
  minWidth: 0,
};

const itemNameStyle = {
  fontSize: 16,
  fontWeight: 500,
  color: '#111827',
  lineHeight: 1.35,
  marginBottom: 6,
};

const itemPriceRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  fontSize: 14,
};

const unitPriceStyle = {
  color: '#475569',
};

const arrowStyle = {
  color: '#94A3B8',
};

const lineTotalStyle = {
  color: '#0F172A',
  fontWeight: 700,
};

const totalFooterStyle = {
  marginTop: 12,
  paddingTop: 16,
  borderTop: '1px solid #CBD5E1',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: 20,
  color: '#111827',
};

const actionsStyle = {
  display: 'flex',
  gap: 10,
  marginTop: 12,
};

const primaryButton = {
  background: '#2563EB',
  color: '#fff',
  border: 'none',
  padding: '12px 16px',
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  cursor: 'pointer',
  fontWeight: 600,
};

const secondaryButton = {
  background: '#F1F5F9',
  color: '#334155',
  border: '1px solid #CBD5E1',
  padding: '12px 16px',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 600,
};

const iconButton = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: '#334155',
};

const messageStyle = {
  marginTop: 10,
  color: '#15803D',
  fontSize: '0.9em',
};

const emptyStateStyle = {
  padding: 24,
  color: '#475569',
  textAlign: 'center',
};